/**
 * UpgradeAwakening — dedicated Awakening tier upgrade page.
 *
 * Linked from three entry points:
 *   1. HealingArcs.tsx  — non-premium upsell button
 *   2. Profile.tsx      — growth-tier Awakening CTA
 *   3. Home.tsx         — "Healing Arcs ✦" nav link for non-premium users
 *
 * Marta attribution placement 2 of 3 (per arcs.ts design constraint):
 *   Arc landing screen (full bio) · Awakening upgrade page (credential) · Affirmations footer (subtle)
 *
 * Layout sections (top → bottom):
 *   1. Header — back button
 *   2. Hero  — dark panel with Tim's positioning headline
 *   3. What's unlocked — feature list
 *   4. Arc preview grid — 6 cards: 2 live + 4 coming soon
 *   5. Marta credential block
 *   6. Pricing section — monthly/annual toggle + Stripe CTA buttons
 *   7. Footer note
 *
 * Premium users who land here are redirected to /healing-arcs immediately.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import { redirectToCheckout } from '../lib/stripe'
import { trackUpgradePromptShown } from '../lib/analytics'

interface UpgradeAwakeningProps {
  user: User
}

// ── Arc preview data ───────────────────────────────────────────────────────────
// The first two entries mirror the live ARCS array (arcs.ts). The remaining
// four are visual-only placeholders — no routes or DB backing. They convey
// the full vision of the arc library without committing to a release timeline.
interface PreviewArc {
  key: string          // catalogue key → upgrade.arcPreviews.<key>.name / .tagline
  color: string
  colorSoft: string
  available: boolean   // false → "Coming Soon" badge
  live: boolean        // true → "Available Now" badge (subset of available)
}

const ARC_PREVIEWS: PreviewArc[] = [
  {
    key: 'letters_to_others',
    color: '#9B72C0',
    colorSoft: 'rgba(155,114,192,0.09)',
    available: true,
    live: true,
  },
  {
    key: 'letters_to_self',
    color: '#C45272',
    colorSoft: 'rgba(196,82,114,0.09)',
    available: true,
    live: true,
  },
  {
    key: 'grief_work',
    color: '#5E8AC4',
    colorSoft: 'rgba(94,138,196,0.09)',
    available: false,
    live: false,
  },
  {
    key: 'boundary',
    color: '#3A9B7A',
    colorSoft: 'rgba(58,155,122,0.09)',
    available: false,
    live: false,
  },
  {
    key: 'reparenting',
    color: '#B87D0A',
    colorSoft: 'rgba(184,125,10,0.09)',
    available: false,
    live: false,
  },
  {
    key: 'homecoming',
    color: '#7B68A8',
    colorSoft: 'rgba(123,104,168,0.09)',
    available: false,
    live: false,
  },
]

// ── Sub-components ─────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2.5 7.5l3 3L11.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

interface ArcPreviewCardProps {
  arc: PreviewArc
}

function ArcPreviewCard({ arc }: ArcPreviewCardProps) {
  const { t } = useTranslation()
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: arc.colorSoft,
        border: `1.5px solid ${arc.color}30`,
        borderTop: `3px solid ${arc.color}`,
        opacity: arc.available ? 1 : 0.72,
      }}
    >
      <div className="px-4 py-4">
        {/* Status badge */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-medium uppercase tracking-widest" style={{ color: arc.color }}>
            {t('arcs.healingArc')}
          </p>
          {arc.live ? (
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: arc.color, color: 'white' }}
            >
              {t('upgrade.availableNow')}
            </span>
          ) : (
            <span
              className="text-[10px] px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.6)', color: '#9B8FAA', border: '1px solid #E4D5EE' }}
            >
              {t('upgrade.comingSoon')}
            </span>
          )}
        </div>

        {/* Arc name */}
        <p
          className="font-display text-base leading-snug mb-1"
          style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}
        >
          {t(`upgrade.arcPreviews.${arc.key}.name`)}
        </p>

        {/* Tagline */}
        <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>
          {t(`upgrade.arcPreviews.${arc.key}.tagline`)}
        </p>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function UpgradeAwakening({ user }: UpgradeAwakeningProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Billing interval toggle — matches the pattern across all in-app upgrade CTAs.
  // Annual is pre-selected: approved 2026-09-05, PRICING_TQO_TIA.md section 12.
  const [interval, setInterval] = useState<'month' | 'year'>('year')
  const [tier, setTier] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [stripeError, setStripeError] = useState<string | null>(null)

  // Load the user's current tier so we can gate the CTA copy correctly
  useEffect(() => {
    db()
      .from('profiles')
      .select('subscription_tier')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const loadedTier = (data as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
        // Redirect premium users to the healing arcs library immediately — nothing to sell
        if (loadedTier === 'premium') {
          navigate('/healing-arcs', { replace: true })
          return
        }
        setTier(loadedTier)
        setLoading(false)
      })
  }, [user.id, navigate])

  async function handleUpgrade() {
    if (checkoutLoading) return
    setCheckoutLoading(true)
    setStripeError(null)
    trackUpgradePromptShown('premium', 'upgrade_awakening')
    const { error } = await redirectToCheckout('premium', interval)
    if (error) {
      setStripeError(error)
      setCheckoutLoading(false)
    }
    // On success window.location.href changes — no cleanup needed
  }

  if (loading) {
    return (
      <div className="min-h-svh flex items-center justify-center" style={{ background: '#FAF7F4' }}>
        <div className="w-5 h-5 rounded-full border-2 border-[#3D1F62] border-t-transparent animate-spin" />
      </div>
    )
  }

  // Monthly price: $29.99 · Annual: $179.99, i.e. $15/mo (saves $180 vs paying monthly for 12 months)
  const monthlyPrice = '$29.99'
  const annualPrice  = '$179.99'
  const annualPerMo  = '$15'
  const annualSaving = '$180'

  const ctaLabel = checkoutLoading
    ? t('common.openingCheckout')
    : interval === 'year'
      ? t('upgrade.beginAwakeningAnnual', { price: annualPrice })
      : t('upgrade.beginAwakeningMonthly', { price: monthlyPrice })

  return (
    <div className="min-h-svh flex flex-col" style={{ background: '#FAF7F4' }}>

      {/* Header */}
      <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6B5F7A' }}
          className="hover:opacity-70 transition-opacity"
          aria-label={t('upgrade.back')}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#9B72C0' }}>
          {t('upgrade.awakening')}
        </p>
      </header>

      <main className="flex-1 px-6 pb-16 max-w-xl mx-auto w-full space-y-6 page-enter">

        {/* ── Hero panel ───────────────────────────────────────────────────── */}
        {/* Dark background, italic display headline, Tim's positioning line.  */}
        <div
          className="rounded-3xl px-6 py-8"
          style={{ background: '#1C1128' }}
        >
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
            {t('upgrade.theAwakeningTier')}
          </p>

          <p
            className="font-display text-2xl leading-snug mb-3"
            style={{ color: '#FAF7F4', fontWeight: 400, fontStyle: 'italic' }}
          >
            {t('upgrade.practiceGives')}
          </p>
          <p
            className="font-display text-2xl leading-snug mb-5"
            style={{ color: '#C4A0E8', fontWeight: 400, fontStyle: 'italic' }}
          >
            {t('upgrade.awakeningGives')}
          </p>

          <p className="text-sm leading-relaxed" style={{ color: '#9B8FAA' }}>
            {t('upgrade.arcIntro')}
          </p>
        </div>

        {/* ── What's unlocked ───────────────────────────────────────────────── */}
        <div
          className="rounded-2xl px-5 py-5"
          style={{
            background: 'rgba(155,114,192,0.05)',
            border: '1.5px solid rgba(155,114,192,0.18)',
          }}
        >
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
            {t('upgrade.everythingInPractice')}
          </p>

          <div className="flex flex-col gap-3">
            {(t('upgrade.awakeningFeatures', { returnObjects: true }) as string[]).map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span style={{ color: '#9B72C0', marginTop: 1 }}>
                  <CheckIcon />
                </span>
                <p className="text-sm leading-relaxed" style={{ color: '#3B2E4A' }}>
                  {item}
                </p>
              </div>
            ))}
          </div>

          {/* Note for free-tier users explaining that Practice is included */}
          {tier === 'free' && (
            <p className="text-xs mt-4 pt-4" style={{ color: '#9B8FAA', borderTop: '1px solid rgba(155,114,192,0.15)' }}>
              {t('upgrade.includesEverything')}
            </p>
          )}
        </div>

        {/* ── Arc preview grid ──────────────────────────────────────────────── */}
        <div>
          <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#6B5F7A' }}>
            {t('upgrade.theHealingArcs')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {ARC_PREVIEWS.map(arc => (
              <ArcPreviewCard key={arc.key} arc={arc} />
            ))}
          </div>
          <p className="text-xs mt-3 text-center" style={{ color: '#C4B5D4' }}>
            {t('upgrade.newArcsAdded')}
          </p>
        </div>

        {/* ── Marta credential ─────────────────────────────────────────────── */}
        {/* Placement 2 of 3 per arcs.ts. Attribution anchor: credential line. */}
        <div
          className="rounded-2xl px-5 py-5"
          style={{
            background: 'rgba(155,114,192,0.04)',
            border: '1.5px solid rgba(155,114,192,0.15)',
          }}
        >
          <div className="flex items-start gap-4">
            {/* Initial monogram */}
            <div
              className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #7B40A8 0%, #9B72C0 100%)' }}
            >
              <span className="font-display text-base" style={{ color: 'white', fontWeight: 500 }}>M</span>
            </div>
            <div>
              <p className="text-sm font-medium mb-0.5" style={{ color: '#1C1128' }}>{t('upgrade.martaName')}</p>
              <p className="text-xs mb-3" style={{ color: '#9B72C0' }}>{t('upgrade.awakenByLove')}</p>
              <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A', lineHeight: 1.7 }}>
                {t('upgrade.martaBio')}
              </p>
            </div>
          </div>
        </div>

        {/* ── Pricing ─────────────────────────────────────────────────────── */}
        <div
          className="rounded-2xl px-5 py-5"
          style={{
            background: 'white',
            border: '1.5px solid #E4D5EE',
          }}
        >
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#6B5F7A' }}>
            {t('upgrade.chooseYourPlan')}
          </p>

          {/* Monthly / Annual toggle */}
          <div
            className="flex rounded-xl mb-5 p-0.5"
            style={{ background: '#F4EFF8' }}
          >
            <button
              onClick={() => setInterval('month')}
              className="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: interval === 'month' ? 'white' : 'transparent',
                color: interval === 'month' ? '#1C1128' : '#9B8FAA',
                border: 'none',
                cursor: 'pointer',
                boxShadow: interval === 'month' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {t('upgrade.monthly')}
            </button>
            <button
              onClick={() => setInterval('year')}
              className="flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5"
              style={{
                background: interval === 'year' ? 'white' : 'transparent',
                color: interval === 'year' ? '#1C1128' : '#9B8FAA',
                border: 'none',
                cursor: 'pointer',
                boxShadow: interval === 'year' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {t('common.annual')}
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{
                  background: interval === 'year' ? '#3D1F62' : '#C4B5D4',
                  color: 'white',
                }}
              >
                {t('upgrade.save', { amount: annualSaving })}
              </span>
            </button>
          </div>

          {/* Price display */}
          <div className="mb-4 text-center">
            {interval === 'month' ? (
              <>
                <span className="font-display text-3xl" style={{ color: '#1C1128', fontWeight: 500 }}>
                  {monthlyPrice}
                </span>
                <span className="text-sm ml-1" style={{ color: '#9B8FAA' }}>{t('common.perMonth')}</span>
              </>
            ) : (
              <>
                <span className="font-display text-3xl" style={{ color: '#1C1128', fontWeight: 500 }}>
                  {annualPerMo}
                </span>
                <span className="text-sm ml-1" style={{ color: '#9B8FAA' }}>{t('common.perMonth')}</span>
                <p className="text-xs mt-1" style={{ color: '#9B8FAA' }}>
                  {t('upgrade.billedAs', { annual: annualPrice, saving: annualSaving })}
                </p>
              </>
            )}
          </div>

          {/* Stripe error */}
          {stripeError && (
            <div className="mb-3 px-4 py-3 rounded-xl" style={{ background: '#FFF3E0', border: '1px solid #FFCC80' }}>
              <p className="text-xs" style={{ color: '#E65100' }}>{stripeError}</p>
            </div>
          )}

          {/* Primary CTA */}
          <button
            onClick={handleUpgrade}
            disabled={checkoutLoading}
            className="w-full py-3.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
            style={{
              background: 'linear-gradient(135deg, #5C2D8A 0%, #9B72C0 100%)',
              color: 'white',
              border: 'none',
              cursor: checkoutLoading ? 'wait' : 'pointer',
            }}
          >
            {ctaLabel}
          </button>

          <p className="text-xs text-center mt-3" style={{ color: '#C4B5D4' }}>
            {t('upgrade.cancelAnytime')}
          </p>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <div className="text-center pb-2">
          <button
            onClick={() => navigate(-1)}
            className="text-sm transition-opacity hover:opacity-70"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B8FAA' }}
          >
            {t('upgrade.maybeLater')}
          </button>
          <p className="text-xs mt-4 leading-relaxed px-4" style={{ color: '#C4B5D4' }}>
            {t('arcs.disclaimer1')}
            {t('arcs.disclaimer2')}
          </p>
        </div>

      </main>
    </div>
  )
}
